import { type FC, useMemo, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { Spinner } from '../../../components/Spinner/Spinner'
import { CustomWorkflowSummary, useListEnabledCustomWorkflowsQuery } from '../../../store/customWorkflow/customWorkflows'
import { type LibraryCatalog, type LibraryFilter } from './libraryCatalog'
import { LibraryFilters } from './LibraryFilters'
import { LibrarySection } from './LibrarySection'
import { ManualWorkflowCard } from './ManualWorkflowCard'
import { TriggeredWorkflowCard } from './TriggeredWorkflowCard'
import { ConfigureWorkflowModal } from './ConfigureWorkflowModal'
import { EnableWorkflowModal } from './EnableWorkflowModal'

interface LibraryWorkflowsProps {
  accountId: string
  active: boolean
  catalog: LibraryCatalog
  isLoading: boolean
  isError: boolean
  onFilterChange: (filter: LibraryFilter) => void
}

export const LibraryWorkflows: FC<LibraryWorkflowsProps> = ({ accountId, active, catalog, isLoading, isError, onFilterChange }) => {
  const { data: enabled, isLoading: enabledLoading, isError: enabledError } = useListEnabledCustomWorkflowsQuery({ accountId }, { skip: !active })
  const filterKey = `${catalog.selected.kind}|${catalog.selected.category}|${catalog.selected.tag}`
  const [selectedManual, setSelectedManual] = useState<CustomWorkflowSummary | null>(null)
  const [selectedTriggered, setSelectedTriggered] = useState<CustomWorkflowSummary | null>(null)

  const enablementByWorkflowId = useMemo(() => {
    const map = new Map<string, string>()
    enabled?.forEach((record) => map.set(record.customWorkflowId, record.id))
    return map
  }, [enabled])

  if (isLoading || enabledLoading) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }
  if (isError || enabledError) {
    return <Alert variant='warning'>Could not load workflows.</Alert>
  }
  if (catalog.manualTotal === 0 && catalog.triggeredTotal === 0 && catalog.hooksTotal === 0) {
    return <div className='py-5 text-center zocks-text-gray-600'>No workflows available yet.</div>
  }

  return (
    <div className='d-flex flex-column gap-4'>
      <LibraryFilters catalog={catalog} onChange={onFilterChange} />
      {catalog.manualTotal > 0 ? (
        <LibrarySection
          title='On-demand'
          subtitle='Start from the corresponding section'
          emptyMessage='No on-demand workflows for this filter.'
          isEmpty={catalog.manual.length === 0}
        >
          {catalog.manual.map((workflow, index) => (
            <ManualWorkflowCard key={`${filterKey}-${workflow.id}`} index={index} workflow={workflow} onClick={() => setSelectedManual(workflow)} />
          ))}
        </LibrarySection>
      ) : null}
      {catalog.triggeredTotal > 0 ? (
        <LibrarySection
          title='Automated'
          subtitle='Run on a schedule or when triggered by a system event'
          emptyMessage='No automated workflows for this filter.'
          isEmpty={catalog.triggered.length === 0}
        >
          {catalog.triggered.map((workflow, index) => (
            <TriggeredWorkflowCard
              key={`${filterKey}-${workflow.id}`}
              index={index}
              workflow={workflow}
              enabled={enablementByWorkflowId.has(workflow.id)}
              onClick={() => setSelectedTriggered(workflow)}
            />
          ))}
        </LibrarySection>
      ) : null}
      {catalog.hooksTotal > 0 ? (
        <LibrarySection
          title='Customizations'
          subtitle='Run automatically before a built-in step, such as an AI feature preset, to adjust its inputs'
          emptyMessage='No customizations for this filter.'
          isEmpty={catalog.hooks.length === 0}
        >
          {catalog.hooks.map((workflow, index) => (
            <TriggeredWorkflowCard
              key={`${filterKey}-${workflow.id}`}
              index={index}
              workflow={workflow}
              enabled={enablementByWorkflowId.has(workflow.id)}
              onClick={() => setSelectedTriggered(workflow)}
            />
          ))}
        </LibrarySection>
      ) : null}
      {selectedManual ? (
        <ConfigureWorkflowModal accountId={accountId} workflow={selectedManual} show={!!selectedManual} onHide={() => setSelectedManual(null)} />
      ) : null}
      {selectedTriggered ? (
        <EnableWorkflowModal
          accountId={accountId}
          workflow={selectedTriggered}
          enablementId={enablementByWorkflowId.get(selectedTriggered.id) ?? null}
          show={!!selectedTriggered}
          onHide={() => setSelectedTriggered(null)}
        />
      ) : null}
    </div>
  )
}
